import { type ReadonlyVec2, vec2 } from 'gl-matrix';

// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments

export type IntersectionPointOfTwoLinesResult = {
  // intersection point
  point: vec2;
  // proportion along line A that intersection occurs, value from  0 - 1
  proportionAlongA: number;
  // proportion along line B that intersection occurs, value from  0 - 1
  proportionAlongB: number;
};

/**
 * Returns details of the intersection point of two lines
 * or undefined if there is no intersection
 *
 * @param lineAStart vec2
 * @param lineAEnd vec2
 * @param lineBStart vec2
 * @param lineBEnd vec2
 * @param allowProjection Defaults to false, if true will permit intersections outside the provided line intervals
 *
 * @returns The intersection point and proportion along each line that the intersection occurs
 */

export function intersectionPointOfTwoLines(
  lineAStart: ReadonlyVec2,
  lineAEnd: ReadonlyVec2,
  lineBStart: ReadonlyVec2,
  lineBEnd: ReadonlyVec2,
  allowProjection = false,
): IntersectionPointOfTwoLinesResult | undefined {
  const [x1, y1] = lineAStart;
  const [x2, y2] = lineAEnd;
  const [x3, y3] = lineBStart;
  const [x4, y4] = lineBEnd;

  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return undefined;
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Lines are parallel
  if (denominator === 0) {
    return undefined;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  // is the intersection along the segments or is projection permitted
  if (!allowProjection && (ua < 0 || ua > 1 || ub < 0 || ub > 1)) {
    return undefined;
  }

  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  return {
    point: vec2.fromValues(x, y),
    proportionAlongA: ua,
    proportionAlongB: ub,
  };
}

/**
 * Returns true if the two lines intersect
 *
 * @param lineAStart
 * @param lineAEnd
 * @param lineBStart
 * @param lineBEnd
 * @returns
 */
export function doLinesIntersect(
  lineAStart: ReadonlyVec2,
  lineAEnd: ReadonlyVec2,
  lineBStart: ReadonlyVec2,
  lineBEnd: ReadonlyVec2,
): boolean {
  const [x1, y1] = lineAStart;
  const [x2, y2] = lineAEnd;
  const [x3, y3] = lineBStart;
  const [x4, y4] = lineBEnd;

  // Fast AABB check
  const line1MinX = Math.min(x1, x2);
  const line1MaxX = Math.max(x1, x2);
  const line1MinY = Math.min(y1, y2);
  const line1MaxY = Math.max(y1, y2);

  const line2MinX = Math.min(x3, x4);
  const line2MaxX = Math.max(x3, x4);
  const line2MinY = Math.min(y3, y4);
  const line2MaxY = Math.max(y3, y4);

  // Check if the bounding boxes of the two lines overlap
  if (
    line1MaxX < line2MinX ||
    line1MinX > line2MaxX ||
    line1MaxY < line2MinY ||
    line1MinY > line2MaxY
  ) {
    // No overlap, lines cannot intersect
    return false;
  }

  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return false;
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Lines are parallel
  if (denominator === 0) {
    return false;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false;
  }

  return true;
}
